import React, { useEffect, useRef } from 'react';
import {
    View,
    Text,
    StyleSheet,
    Modal,
    TouchableOpacity,
    Image,
    Animated,
    Dimensions,
} from 'react-native';
import LinearGradient from '../../src/components/LinearGradient';

interface AccountVerifiedModalProps {
    visible: boolean;
    accountNumber: string;
    platformName: string;
    // When false, the account was saved but the backend hasn't verified it yet
    // (verification is asynchronous), so we show a "pending" state instead.
    verified?: boolean;
    onGotIt: () => void;
}

export default function AccountVerifiedModal({
    visible,
    accountNumber,
    platformName,
    verified = true,
    onGotIt,
}: AccountVerifiedModalProps) {
    const fadeAnim = useRef(new Animated.Value(0)).current;
    const scaleAnim = useRef(new Animated.Value(0.8)).current;

    useEffect(() => {
        if (visible) {
            Animated.parallel([
                Animated.timing(fadeAnim, {
                    toValue: 1,
                    duration: 300,
                    useNativeDriver: true,
                }),
                Animated.spring(scaleAnim, {
                    toValue: 1,
                    tension: 50,
                    friction: 7,
                    useNativeDriver: true,
                }),
            ]).start();
        } else {
            fadeAnim.setValue(0);
            scaleAnim.setValue(0.8);
        }
    }, [visible, fadeAnim, scaleAnim]);

    return (
        <Modal
            visible={visible}
            transparent
            animationType="none"
            statusBarTranslucent
            onRequestClose={onGotIt}
        >
            <Animated.View style={[styles.overlay, { opacity: fadeAnim }]}>
                <Animated.View
                    style={[
                        styles.cardContainer,
                        {
                            transform: [{ scale: scaleAnim }],
                            opacity: fadeAnim,
                        },
                    ]}
                >
                    <LinearGradient
                        colors={['#2a0d3a', '#1a0526', '#0a0012']}
                        start={{ x: 0.5, y: 0 }}
                        end={{ x: 0.5, y: 1 }}
                        style={styles.card}
                    >
                        {/* Status badge */}
                        <View style={styles.badgeContainer}>
                            <Image
                                source={
                                    verified
                                        ? require('../Assets/completeIcon.png')
                                        : require('../Assets/pendingIcon.png')
                                }
                                style={styles.badge}
                                resizeMode="contain"
                            />
                        </View>

                        {/* Title */}
                        <Text style={[styles.title, !verified && styles.titlePending]}>
                            {verified ? 'Account Verified!' : 'Verification Pending'}
                        </Text>

                        {/* Description */}
                        <Text style={styles.description}>
                            Account number{' '}
                            <Text style={styles.highlightText}>'{accountNumber}'</Text> on{' '}
                            <Text style={styles.highlightText}>{platformName}</Text>{' '}
                            {verified
                                ? 'verified successfully.'
                                : 'was added and is pending verification.'}
                        </Text>

                        {/* Got it button */}
                        <TouchableOpacity
                            activeOpacity={0.85}
                            onPress={onGotIt}
                            style={styles.buttonWrapper}
                        >
                            <LinearGradient
                                colors={['#FF4D9D', '#9B4D96', '#4D79FF']}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 0 }}
                                style={styles.button}
                            >
                                <Text style={styles.buttonText}>Got it</Text>
                            </LinearGradient>
                        </TouchableOpacity>
                    </LinearGradient>
                </Animated.View>
            </Animated.View>
        </Modal>
    );
}

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const CARD_WIDTH = SCREEN_WIDTH * 0.88;

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    cardContainer: {
        width: CARD_WIDTH,
        maxWidth: 460,
    },
    card: {
        borderRadius: 24,
        borderWidth: 1.5,
        borderColor: 'rgba(155, 77, 150, 0.4)',
        paddingVertical: 32,
        paddingHorizontal: 24,
        alignItems: 'center',
        shadowColor: '#9B4D96',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.3,
        shadowRadius: 20,
        elevation: 15,
    },
    badgeContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 8,
        marginBottom: 24,
    },
    badge: {
        width: 96,
        height: 96,
    },
    title: {
        fontSize: 26,
        fontFamily: 'Lato-Bold',
        color: '#5CB338',
        textAlign: 'center',
        marginBottom: 16,
    },
    titlePending: {
        color: '#E6A700',
    },
    description: {
        fontSize: 16,
        fontFamily: 'Lato-Regular',
        color: '#cfcfcf',
        textAlign: 'center',
        lineHeight: 24,
        marginBottom: 28,
        paddingHorizontal: 6,
    },
    highlightText: {
        color: '#fff',
        fontFamily: 'Lato-Bold',
    },
    buttonWrapper: {
        width: '100%',
        shadowColor: '#FF4D9D',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.4,
        shadowRadius: 12,
        elevation: 8,
    },
    button: {
        paddingVertical: 16,
        borderRadius: 28,
        alignItems: 'center',
        justifyContent: 'center',
    },
    buttonText: {
        fontSize: 18,
        fontFamily: 'Lato-Bold',
        color: '#fff',
        letterSpacing: 0.5,
    },
});